import { ChangeDetectionStrategy, Component, inject, viewChild, input } from '@angular/core';
import { VgApiService, VgCoreModule } from '@videogular/ngx-videogular/core'
import { Observable } from 'rxjs';

import * as Sentry from '@sentry/angular';

import { StudentDataService } from 'src/app/core/services/student-data.service';

@Component({
    selector: 'instructions',
    templateUrl: './instructions.component.html',
    styleUrls: ['./instructions.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [VgCoreModule]
})
export class InstructionsComponent {
  private studentDataService = inject(StudentDataService);
  vgApiService = inject(VgApiService);


  readonly videoSource = input<string>('');

  readonly videoPlayerElement = viewChild<HTMLVideoElement | null>('videoPlayerElement');

  playerAPI: VgApiService;
  viewInstructionalVideo: boolean = false;
  requiresManualPlay: boolean = false;

  constructor() {
    const vgApiService = this.vgApiService;

    this.playerAPI = vgApiService ;
  }

  onPlayerReady(API: VgApiService): void {
    //this.playerAPI = API.getDefaultMedia();
    this.playerAPI = API ;
    this.playerAPI.getDefaultMedia().subscriptions.ended.subscribe(
      () => {
        this.playerAPI.currentTime=0;
      }
    );

    this.playerAPI.getDefaultMedia().subscriptions.pause.subscribe(
      () => {
        this.onUpdateState();
      }
    );

    this.playerAPI.getDefaultMedia().subscriptions.playing.subscribe(
      () => {
        this.requiresManualPlay = false ;
      }
    );
  }

  onUpdateState(): void {
    if (this.playerAPI.state === 'paused')
    {
      if (this.videoPlayerElement != null && this.videoPlayerElement())
      {
        // iOS - if student exits full screen mode by using pinch gestures,
        // we'll automatically stop the video and return to the screen to
        // prevent weird scenarios where the student is stuck in the video screen
        if (document.fullscreenElement)
        {
          document.exitFullscreen().then(() => {
            console.log("exiting fullscreen");
          }
          ).catch((err) => {
            console.log(err);
          })
        }
        // Future idea: Find a way to pause the video so we can
        // continue playing where we left off instead of having to start
        // over again.
        // if (stopVideoOnPause) {
        this.playerAPI.pause();
        //}
      }
    }
  }

  // Play and show the instructional video, if we have our player API loaded
  playInstructionalVideo(): Observable<void> {
    return new Observable((observer) => {
      // This is need this for when the video is skipped for DEMO users to continue
      // on as expected.
      // NOTE If we ever want to add a pause functionality to these videos we will have to rethink this
      let pauseSubscription = this.playerAPI.getDefaultMedia().subscriptions.pause.subscribe(() => {
        observer.next();
        observer.complete();
        this.viewInstructionalVideo = false;
      });

      let errorSubscription = this.playerAPI.getDefaultMedia().subscriptions.error.subscribe((error) => {
        this.requiresManualPlay = true ;
        Sentry.captureException(error, {
          tags: {
            section: 'instructions',
            action: 'play-instructional-video',
          },
          contexts: {
            data: {
              videoSource: this.videoSource()
            }
          }
        });
        observer.error(error);
        this.viewInstructionalVideo = false;
      });

      let suspendSubscription = this.playerAPI.getDefaultMedia().subscriptions.suspend.subscribe(() => {
        this.requiresManualPlay = !this.playerAPI.getDefaultMedia().canPlay ;
      });

      this.requiresManualPlay = false;
      this.viewInstructionalVideo = true;
      this.playerAPI.play();

      return {
        unsubscribe: () => {
          pauseSubscription.unsubscribe();
          errorSubscription.unsubscribe();
          suspendSubscription.unsubscribe();
        }
      }
    });
  }

  manuallyPlayVideo() {
    if (this.requiresManualPlay)
    {
      this.playInstructionalVideo().subscribe() ;
    }
  }

  skipVideo(event: any): void {
    event.stopPropagation() ;
    if (this.studentDataService.isUserSuperUser() || this.studentDataService.isDemoUser())
    {
      if (this.playerAPI != null)
      {
        this.playerAPI.pause();
        // reset player time to zero if skipped for replay
        this.playerAPI.currentTime = 0;
      }
    }
  }

  replayVideo(): void {
    this.playInstructionalVideo().subscribe();
  }
}
